/**
 * Minimal decoder for protobuf-encoded `foxglove.CompressedVideo` messages.
 *
 * The reader has no general protobuf deserialization support (see serde.ts),
 * but compressed-video topics are commonly recorded with the Foxglove SDK,
 * which writes protobuf. Rather than pull in a protobuf runtime and schema
 * compiler for one fixed, four-field message, this module hand-decodes the
 * wire format.
 *
 * Schema (https://docs.foxglove.dev/docs/sdk/schemas/compressed-video):
 *   field 1: timestamp (message: 1 = sec varint, 2 = nsec varint)
 *   field 2: frame_id (string)
 *   field 3: data (bytes — Annex B-framed video, exactly one frame)
 *   field 4: format (string — "h264", "h265", "vp9", or "av1")
 */

import { decodeTimestamp, WireReader, WireType } from "./protobufWire";

export const FOXGLOVE_COMPRESSED_VIDEO_SCHEMA_NAME = "foxglove.CompressedVideo";

export interface FoxgloveCompressedVideo {
  timestamp: { sec: number; nsec: number };
  frame_id: string;
  data: Uint8Array;
  format: string;
}

const textDecoder = new TextDecoder();

export function decodeCompressedVideoMessage(
  view: ArrayBufferView,
): FoxgloveCompressedVideo {
  const reader = new WireReader(
    new Uint8Array(view.buffer, view.byteOffset, view.byteLength),
  );
  const message: FoxgloveCompressedVideo = {
    timestamp: { sec: 0, nsec: 0 },
    frame_id: "",
    data: new Uint8Array(),
    format: "",
  };
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    if (wireType !== WireType.LengthDelimited) {
      reader.skip(wireType);
      continue;
    }
    const value = reader.bytes(reader.varint());
    switch (fieldNumber) {
      case 1:
        message.timestamp = decodeTimestamp(value);
        break;
      case 2:
        message.frame_id = textDecoder.decode(value);
        break;
      case 3:
        message.data = value;
        break;
      case 4:
        message.format = textDecoder.decode(value);
        break;
      default:
        break;
    }
  }
  return message;
}
